import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import cx from 'classnames';
import { MapPin } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';

import { actions as locationActions, duck as locationsDuck } from 'coupon-common/src/modules/locations';

import styles from './CityPickerButtonModal.module.scss';

const provincesKey = locationsDuck.provincesEntity.key;

/**
 * Button + popover for picking one or more cities to filter a coupon feed
 * by — used by CouponsListAll. Lets the user choose a province, search/pick
 * cities within it (via the debounced `getFilteredCities` action — the
 * `cities` module's own getCityList is province-scoped only, with no
 * "search every city regardless of province" capability anywhere), and
 * accumulate a selection across possibly multiple provinces before
 * confirming.
 *
 * Props:
 *   label            — trigger button label
 *   selectedCities    — array of { id, name } currently selected (pending,
 *                       not yet confirmed)
 *   onChangeSelected  — called with the updated pending selection array
 *   onConfirm         — called when the user confirms the popover
 *   confirmLabel      — confirm button label
 *   variant           — 'chip' (default, existing standalone pill/border
 *                       look) or 'bare' (no background/border/shadow, fills
 *                       100% width — for embedding inside a parent unified
 *                       search bar, e.g. CouponsListAll's search-bar pill).
 *                       Purely visual; popover/behaviour is identical.
 *   singleSelect      — false (default) preserves the original multi-city
 *                       accumulate-then-confirm behaviour (CouponsListAll's
 *                       use case: pick several cities across provinces,
 *                       hit confirmLabel to commit them all at once).
 *                       true switches to pick-one-and-done (Home's hero
 *                       location field): clicking a city replaces any
 *                       existing selection, immediately calls onConfirm
 *                       with that single-city array, and closes the
 *                       popover — no separate confirm button is rendered.
 *   autoConfirm       — false (default) requires the confirmLabel button to
 *                       be pressed to commit the pending selection
 *                       (CouponsListAll's use case). true commits on every
 *                       checkbox toggle/chip removal — onConfirm fires
 *                       immediately with the updated array each time, so no
 *                       button press is needed. Multi-select still works
 *                       normally (the popover stays open between picks so
 *                       several cities can be chosen); the confirmLabel
 *                       button still renders as a "Done" affordance to
 *                       close the popover, it's just no longer required to
 *                       commit anything. Independent of singleSelect —
 *                       don't combine the two.
 */

// Trigger label shows at most this many city names before collapsing the
// rest into a "... (total)" suffix — e.g. "Halifax, Antigonish, Bedford
// ... (8)" instead of listing all 8 names inline.
const MAX_VISIBLE_CITY_NAMES = 3;

const CityPickerButtonModal = ({
  label = 'City',
  selectedCities = [],
  onChangeSelected,
  onConfirm,
  confirmLabel = 'Confirm',
  variant = 'chip',
  singleSelect = false,
  autoConfirm = false,
}) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [provinceId, setProvinceId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [coords, setCoords] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // The popover is portaled to document.body (see render below) so it can
  // never be clipped by an ancestor's overflow/stacking context — it's
  // positioned in fixed coordinates derived from the trigger button instead
  // of relying on `position: absolute` inside the normal DOM flow.
  const updateCoords = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, left: rect.left });
  };

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    updateCoords();

    const handleReposition = () => updateCoords();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const locations = useSelector(state => state.locations);
  const provinceOptions = locations.result?.[provincesKey]?.results?.map(id => locations.entities[provincesKey]?.[id]) || [];

  const citiesState = useSelector(state => state.locations.result?.[locationsDuck.citiesEntity.key]);
  const cityEntities = useSelector(state => state.locations.entities?.[locationsDuck.citiesEntity.key]) || {};
  const cityOptions = citiesState?.results?.map(id => cityEntities[id]).filter(Boolean) || [];

  useEffect(() => {
    dispatch(locationActions.getProvinces());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = e => {
      const clickedTrigger = wrapRef.current && wrapRef.current.contains(e.target);
      const clickedPopover = popoverRef.current && popoverRef.current.contains(e.target);
      if (!clickedTrigger && !clickedPopover) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const province = provinceOptions.find(p => String(p?.id) === String(provinceId));
    if (province) {
      dispatch(locationActions.getFilteredCities(province.name, searchText));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinceId, searchText]);

  const isSelected = city => selectedCities.some(c => c.id === city.id);

  const toggleCity = city => {
    if (singleSelect) {
      // Pick-one-and-done: clicking the already-selected city just clears
      // it (no onConfirm — nothing new to commit). Clicking any other city
      // replaces the selection outright, commits it immediately, and
      // closes the popover, since there's no separate confirm step here.
      if (isSelected(city)) {
        onChangeSelected([]);
        return;
      }
      const updated = [{ id: city.id, name: city.name }];
      onChangeSelected(updated);
      onConfirm(updated);
      setIsOpen(false);
      return;
    }

    const updated = isSelected(city)
      ? selectedCities.filter(c => c.id !== city.id)
      : [...selectedCities, { id: city.id, name: city.name }];
    onChangeSelected(updated);
    // autoConfirm: commit every toggle immediately (no button press
    // needed), but leave the popover open — unlike singleSelect, the point
    // here is picking several cities in one sitting.
    if (autoConfirm) {
      onConfirm(updated);
    }
  };

  const removeChip = id => {
    const updated = selectedCities.filter(c => c.id !== id);
    onChangeSelected(updated);
    if (autoConfirm) {
      onConfirm(updated);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedCities);
    setIsOpen(false);
  };

  // Builds the trigger button's label text: falls back to `label` when
  // nothing's selected, otherwise shows up to MAX_VISIBLE_CITY_NAMES city
  // names followed by the total count — collapsing the rest into "..."
  // once there are more selections than that, e.g.
  // "Halifax, Antigonish, Bedford ... (8)".
  const getTriggerLabel = () => {
    if (selectedCities.length === 0) return label;

    const visibleNames = selectedCities.slice(0, MAX_VISIBLE_CITY_NAMES).map(c => c.name).join(', ');
    const suffix = selectedCities.length > MAX_VISIBLE_CITY_NAMES ? ' ...' : '';

    return `${visibleNames}${suffix} (${selectedCities.length})`;
  };

  return (
    <div className={styles.wrap} ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        ref={triggerRef}
        className={cx(styles.triggerBtn, variant === 'bare' && styles.triggerBtnBare)}
        onClick={() => setIsOpen(!isOpen)}
      >
        <MapPin size={16} className={variant === 'bare' ? styles.triggerIconBare : undefined} />
        <span className={variant === 'bare' ? styles.triggerLabelBare : undefined}>
          {getTriggerLabel()}
        </span>
      </button>

      {isOpen && coords && createPortal(
        <div
          ref={popoverRef}
          className={styles.popover}
          style={{ position: 'fixed', top: coords.top, left: coords.left }}
        >
          {selectedCities.length > 0 && (
            <div className={styles.selectedChips}>
              {selectedCities.map(city => (
                <span key={city.id} className={styles.chip}>
                  {city.name}
                  <button type="button" className={styles.chipRemove} onClick={() => removeChip(city.id)} aria-label="Remove">
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <label className={styles.fieldLabel} htmlFor="city-picker-province">
            Province
          </label>
          <select
            id="city-picker-province"
            className={styles.select}
            value={provinceId}
            onChange={e => setProvinceId(e.target.value)}
          >
            <option value="" disabled>
              Select a province
            </option>
            {provinceOptions.map(province => (
              <option key={province.id} value={province.id}>
                {province.name}
              </option>
            ))}
          </select>

          {provinceId && (
            <>
              <label className={styles.fieldLabel} htmlFor="city-picker-search">
                City
              </label>
              <input
                id="city-picker-search"
                type="text"
                className={styles.input}
                placeholder="Search cities..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />

              <div className={styles.optionsList}>
                {cityOptions.map(city => (
                  <label key={city.id} className={styles.optionRow}>
                    <input type="checkbox" checked={isSelected(city)} onChange={() => toggleCity(city)} />
                    {city.name}
                  </label>
                ))}
              </div>
            </>
          )}

          {!singleSelect && (
            <button type="button" className={styles.confirmBtn} onClick={handleConfirm}>
              {confirmLabel}
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default CityPickerButtonModal;